import { memo, useEffect } from "react";
import { useChatSession } from "../hooks/useChatSession";
import ChatAccessDenied from "./ChatAccessDenied";
import ChatComposer from "./ChatComposer";
import ChatHeader from "./ChatHeader";
import ChatMessagesArea from "./ChatMessagesArea";
import ChatThreadsPanel from "./ChatThreadsPanel";

const BigWindow = ({ setOpen }) => {
  const session = useChatSession({
    initialHistoryOpen: true,
    historyMode: "sidebar",
  });

  useEffect(() => {
    document.body.classList.add("overflow-hidden");
    return () => {
      document.body.classList.remove("overflow-hidden");
    };
  }, []);

  if (!session.hasViewPermission) {
    return <ChatAccessDenied onClose={() => setOpen(false)} variant="modal" />;
  }

  return (
    <div className="fixed inset-0 z-[800] flex items-center justify-center bg-black/60 p-4 sm:p-6 md:p-8 animate-fade-in">
      <div
        className="fixed inset-0 cursor-pointer"
        onClick={() => setOpen(false)}
        aria-hidden="true"
      />

      <section className="w-full max-w-6xl h-[85vh] bg-surface rounded-2xl shadow-2xl flex flex-col overflow-hidden relative z-10 border border-border-light">
        <ChatHeader
          t={session.t}
          variant="expanded"
          isHistoryOpen={session.isHistoryOpen}
          onNewChat={session.handleNewChat}
          onToggleHistory={session.toggleHistory}
          onClose={() => setOpen(false)}
        />

        <div className="flex-1 flex overflow-hidden bg-surface-muted relative">
          {session.isHistoryOpen && (
            <ChatThreadsPanel
              variant="sidebar"
              isOpen={session.isHistoryOpen}
              onClose={() => session.setIsHistoryOpen(false)}
              onCloseChat={() => setOpen(false)}
              searchQuery={session.searchQuery}
              onSearchChange={session.setSearchQuery}
              groupedThreads={session.groupedThreads}
              activeThreadId={session.activeThreadId}
              threadsFetching={session.threadsFetching}
              threadsLoading={session.threadsLoading}
              threadsPage={session.threadsPage}
              threadsContainerRef={session.threadsContainerRef}
              onThreadsScroll={session.handleThreadsScroll}
              onSelectThread={session.handleSelectThread}
              onCopyThread={session.handleCopyThread}
              onDeleteThread={session.handleDeleteThread}
              getThreadUuid={session.getThreadUuid}
            />
          )}

          <div className="flex-1 flex flex-col overflow-hidden relative">
            <ChatMessagesArea
              t={session.t}
              variant="expanded"
              messages={session.messages}
              loading={session.loading}
              loadingMore={session.loadingMore}
              fetchLoading={session.fetchLoading}
              isFetching={session.isFetching}
              hasPromptPermission={session.hasPromptPermission}
              messagesContainerRef={session.messagesContainerRef}
              onScroll={session.handleScroll}
              onSuggestionClick={session.handleSuggestionClick}
              messageTextClassName="big-chat-message-text"
            />

            <ChatComposer
              t={session.t}
              variant="expanded"
              inputValue={session.inputValue}
              onInputChange={session.setInputValue}
              onKeyDown={session.handleKeyPress}
              onSend={() => session.handleSendMessage()}
              onFileSelect={session.handleFileSelect}
              onFileChange={session.handleFileChange}
              onRemoveFile={session.handleRemoveFile}
              fileInputRef={session.fileInputRef}
              selectedFile={session.selectedFile}
              loading={session.loading}
              hasPromptPermission={session.hasPromptPermission}
            />
          </div>
        </div>
      </section>
    </div>
  );
};

export default memo(BigWindow);
